import React from "react";
import { clsx } from "clsx";

import { Collapsible as RadixCollapsible } from "radix-ui";

import { twMerge } from "@/design-system/twMerge";

export interface CollapsibleProps {
  open: boolean;
  children: React.ReactNode;
  /**
   * Fires once the open animation finishes, not when the region closes. It
   * depends on the open keyframe actually running: it does not fire on the
   * first render when `open` starts true, or if the animation is disabled
   * (for example `animation: none` under prefers-reduced-motion).
   */
  onOpenAnimationEnd?: () => void;
  /** Element wrapping the region; use "li" when it sits directly in a list. */
  as?: "div" | "li";
  /** Applied to the animated content region. */
  className?: string;
}

/**
 * A controlled, animated content region for callers that render their own
 * header and keep the open state themselves. Children unmount while closed.
 */
export function Collapsible({
  open,
  children,
  onOpenAnimationEnd,
  as: Component = "div",
  className,
}: CollapsibleProps) {
  const handleAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>) => {
    // animationend bubbles, so ignore animations that ran inside the content.
    if (open && event.target === event.currentTarget) {
      onOpenAnimationEnd?.();
    }
  };

  return (
    <RadixCollapsible.Root open={open} asChild>
      <Component>
        <RadixCollapsible.Content
          onAnimationEnd={handleAnimationEnd}
          className={twMerge(
            clsx(
              "overflow-hidden",
              "data-[state=open]:animate-radixCollapsibleOpen",
              "data-[state=closed]:animate-radixCollapsibleClose",
              className,
            ),
          )}
        >
          {children}
        </RadixCollapsible.Content>
      </Component>
    </RadixCollapsible.Root>
  );
}
